import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useTheme } from "next-themes";
import { cn } from "@repo/blacksmith-ui/cn";
import { assetUrl } from "@/lib/assetUrl";
import { useTickerTransition } from "./useTickerTransition";
import { ANIMATION_VARIANTS, ANVIL_ANIMATION_ASSETS, anvilAnimationFile, seededAnvilActiveVariant, seededIndex, type AnvilActiveVariant, type AnvilAnimationVariant, type AnvilTheme } from "@repo/blacksmith-ui/anvil/animations";
export { ANIMATION_VARIANTS, ANVIL_ANIMATION_ASSETS, seededAnvilActiveVariant, type AnvilActiveVariant };
const STATUS_WORDS = ["Thinking", "Pondering", "Forging", "Hammering", "Shaping", "Drafting", "Analyzing", "Synthesizing", "Constructing", "Assembling", "Conjuring", "Refining", "Brewing", "Sculpting", "Weaving", "Polishing", "Tinkering", "Calibrating", "Designing", "Iterating", "Compiling", "Computing", "Deciphering", "Evaluating", "Exploring", "Formulating", "Investigating", "Mulling", "Processing", "Reasoning", "Strategizing", "Contemplating", "Deliberating", "Devising", "Envisioning", "Imagining", "Mapping", "Orchestrating", "Plotting", "Preparing", "Puzzling", "Ruminating", "Scheming", "Smelting", "Sparking", "Tempering", "Welding", "Wiring", "Crunching", "Hatching"] as const;

/** Versioned dashboard URL for an anvil animation; see {@link anvilAnimationFile}. */
export function anvilAnimationSrc(animationVariant: AnvilAnimationVariant, activeVariant: AnvilActiveVariant, themeSuffix: AnvilTheme): string {
  return assetUrl(`anvil-animations/${anvilAnimationFile(animationVariant, activeVariant, themeSuffix)}`);
}
export function AnvilWorkingStatus(t0) {
  const $ = _c(32);
  const {
    seed,
    animationVariant: t1,
    showLabel: t2,
    animateIn: t3,
    animateOut: t4,
    statusLabel,
    labelExtra,
    labelToggle
  } = t0;
  const animationVariant = t1 === undefined ? "active" : t1;
  const showLabel = t2 === undefined ? true : t2;
  const animateIn = t3 === undefined ? true : t3;
  const animateOut = t4 === undefined ? false : t4;
  const {
    resolvedTheme
  } = useTheme();
  const [randomVariant] = useState(_temp);
  const [randomWord] = useState(_temp2);
  const hasSeed = seed !== undefined;
  let t5;
  if ($[0] !== hasSeed || $[1] !== randomVariant || $[2] !== seed) {
    t5 = hasSeed ? seededAnvilActiveVariant(seed) : randomVariant;
    $[0] = hasSeed;
    $[1] = randomVariant;
    $[2] = seed;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const activeVariant = t5;
  let t6;
  if ($[4] !== hasSeed || $[5] !== randomWord || $[6] !== seed) {
    t6 = hasSeed ? STATUS_WORDS[seededIndex(`${seed}:word`, STATUS_WORDS.length)] : randomWord;
    $[4] = hasSeed;
    $[5] = randomWord;
    $[6] = seed;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const seededWord = t6;
  let t7;
  if ($[8] !== seededWord || $[9] !== statusLabel) {
    t7 = statusLabel?.trim() || seededWord || "Thinking";
    $[8] = seededWord;
    $[9] = statusLabel;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const word = t7;
  const suffix = resolvedTheme === "dark" ? "dark" : "light";
  let t8;
  if ($[11] !== activeVariant || $[12] !== animationVariant || $[13] !== suffix) {
    t8 = anvilAnimationSrc(animationVariant, activeVariant, suffix);
    $[11] = activeVariant;
    $[12] = animationVariant;
    $[13] = suffix;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const src = t8;
  let t9;
  if ($[15] !== word) {
    t9 = <RollingLabel text={word} className="type-body-s-regular text-neutral-900 dark:text-neutral-100" />;
    $[15] = word;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== labelExtra || $[18] !== t9) {
    t10 = <>{t9}{labelExtra}</>;
    $[17] = labelExtra;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const label = t10;
  const t11 = animateOut ? "animate-anvil-exit" : animateIn && "animate-anvil-enter";
  let t12;
  if ($[20] !== t11) {
    t12 = cn("flex items-center gap-1.5 py-1", t11);
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== src) {
    t13 = <img src={src} alt="" width={40} height={40} draggable={false} className="select-none" />;
    $[22] = src;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== label || $[25] !== labelToggle || $[26] !== showLabel) {
    t14 = showLabel && labelToggle ? <button type="button" onClick={labelToggle.onToggle} aria-expanded={labelToggle.expanded} data-testid="anvil-label-toggle" className="flex cursor-pointer items-center gap-1.5 rounded-sm hover:opacity-80">{label}</button> : showLabel ? label : null;
    $[24] = label;
    $[25] = labelToggle;
    $[26] = showLabel;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t12 || $[29] !== t13 || $[30] !== t14) {
    t15 = <div className={t12}>{t13}{t14}</div>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  return t15;
}

/**
 * Status line that rolls to a new label: both labels travel a full line in
 * lockstep through a vertical clip (`label-ticker-*` in globals.css). The
 * first label renders as-is; only changes animate.
 */
function _temp2() {
  return STATUS_WORDS[Math.floor(Math.random() * STATUS_WORDS.length)];
}
function _temp() {
  return ANIMATION_VARIANTS[Math.floor(Math.random() * ANIMATION_VARIANTS.length)];
}
function RollingLabel(t0) {
  const $ = _c(14);
  const {
    text,
    className
  } = t0;
  const {
    current,
    previous
  } = useTickerTransition(text);
  let t1;
  if ($[0] !== className) {
    t1 = cn("inline-grid overflow-y-clip [--label-ticker-travel:100%]", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== previous) {
    t2 = previous !== null && <span key={`out:${previous}`} aria-hidden={true} className="[grid-area:1/1] label-ticker-exit">{previous}</span>;
    $[2] = previous;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `in:${current}`;
  const t4 = previous !== null && "label-ticker-enter";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("[grid-area:1/1]", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== current || $[7] !== t3 || $[8] !== t5) {
    t6 = <span key={t3} className={t5}>{current}</span>;
    $[6] = current;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t6) {
    t7 = <span className={t1}>{t2}{t6}</span>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}